import moment from "moment";
import * as R from "ramda";
import {
  DriverRequests,
  ExceptionsAndOpportunity,
  StopType,
  LoadTime,
} from "../enums/RouteFilterTypes";
import { prepareDriver } from "./driver";
import { defaultOrderFn } from "../features/routeDetail/model";
import { isEmptyOdfDriver } from "../features/driverDetails/model";

export const FilterTypes = {
  routeStatus: "routeStatus",
  exceptionsAndOpportunity: "exceptionsAndOpportunity",
  stopType: "stopType",
  driverRequests: "driverRequests",
  loadTime: "loadTime",
};

const includesExceptionsAndOpportunity = (filters, route) =>
  filters.some((type) => {
    switch (type) {
      case ExceptionsAndOpportunity.Postcode:
        return route.postcodeExceptions > 0;
      case ExceptionsAndOpportunity.Duty:
        return route.dutyTimeExceptions > 0 || route.legalDutyExceptions > 0;
      case ExceptionsAndOpportunity.Service:
        return route.serviceExceptions > 0;
      case ExceptionsAndOpportunity.Opportunity:
        return route.opportunityTime > 0;
      default:
        return false;
    }
  });

const includesStopType = (filters, route) =>
  filters.some((type) => {
    switch (type) {
      case StopType.TenThirty:
        return route.stopStats.totalBy1030 > 0;
      case StopType.Twelve:
        return route.stopStats.totalBy12 > 0;
      case StopType.Evening:
        return route.stopStats.totalEvening > 0;
      default:
        return false;
    }
  });

const includesDriverRequests = (filters, route) =>
  filters.some((type) => {
    if (type === DriverRequests.Requests) {
      return route.totalOpenRequests > 0;
    }

    return false;
  });

const includesLoadTime = (filters, route) =>
  filters.some((type) => {
    switch (type) {
      case LoadTime.OnSchedule:
        return (
          route.firstParcelScanTime != null &&
          !route.lowLoadRate &&
          !route.exceededLoadTime
        );
      case LoadTime.BehindSchedule:
        return route.lowLoadRate;
      case LoadTime.ScanTimeExceeded:
        return route.exceededLoadTime;
      default:
        return false;
    }
  });

export const filterRoutes = (filters, routes = []) =>
  routes.filter((route) => {
    if (
      filters[FilterTypes.routeStatus] &&
      !filters[FilterTypes.routeStatus].includes(route.routeStatus)
    ) {
      return false;
    }

    if (
      filters[FilterTypes.exceptionsAndOpportunity] &&
      !includesExceptionsAndOpportunity(
        filters[FilterTypes.exceptionsAndOpportunity],
        route
      )
    ) {
      return false;
    }

    if (
      filters[FilterTypes.stopType] &&
      !includesStopType(filters[FilterTypes.stopType], route)
    ) {
      return false;
    }

    if (
      filters[FilterTypes.driverRequests] &&
      !includesDriverRequests(filters[FilterTypes.driverRequests], route)
    ) {
      return false;
    }

    if (
      filters[FilterTypes.loadTime] &&
      !includesLoadTime(filters[FilterTypes.loadTime], route)
    ) {
      return false;
    }

    return true;
  });

export const updateStopInStopList = (stops, updatedStop) => {
  const updatedStopIndex = stops.findIndex(
    (s) => s.stopNumber === updatedStop.stopNumber
  );
  const updatedStops = stops.slice();
  updatedStops[updatedStopIndex] = updatedStop;
  return updatedStops;
};

export const prepareRoute = (formValues, userId, multiRunRoute = false) => ({
  userId,
  multiRunRoute,
  date: moment.utc().toDate().getTime(),
  driver: prepareDriver(formValues),
  routeCode: formValues.routeNumber,
  sectionCode: formValues.sectionCode || null,
  odfRouteOwner: !isEmptyOdfDriver(formValues.odfDriver)
    ? formValues.odfDriver.driverCode
    : null,
});

export const sortRoutesBySectionRoute = (routes) => {
  const bySectionRoute = R.descend(R.prop("sectionRoute"));
  return R.sort(bySectionRoute, routes);
};

export const prepareNoStopNumberRouteStops = (stops) => {
  const sortedStops = stops.sort((previous, next) =>
    defaultOrderFn(previous, next, "sequenceNumber")
  );

  const originalStopsData = [];
  const updatedStops = sortedStops.map((stop, i) => {
    const stopNumber = `${i + 1}`;
    originalStopsData.push({
      stopId: stop.stopId,
      stopNumber: stopNumber,
    });
    return {
      ...stop,
      displayNumber: stopNumber,
      stopNumber,
      sequenceNumber: stopNumber,
    };
  });

  return { originalStopsData, updatedStops };
};

const getAvailableStopNumbers = (stopNumbers) =>
  [...Array(999).keys()].filter((el) => el && !stopNumbers.includes(el));

export const updateStopsWithValidStopNumber = (stops) => {
  const sortedStops = stops.sort((previous, next) =>
    defaultOrderFn(previous, next, "sequenceNumber")
  );
  let availableStopNumbers = getAvailableStopNumbers(
    sortedStops.map((stop) => stop.stopNumber)
  );
  let originalStopsData = [];
  const updatedStops = sortedStops.map((stop, i) => {
    if (!stopWithValidStopNumber(stop)) {
      const stopNumber = availableStopNumbers.shift();
      if (stopNumber) {
        originalStopsData.push({
          stopId: stop.stopId,
          stopNumber: `${stopNumber}`,
        });
        return {
          ...stop,
          displayNumber: `${stopNumber}`,
          stopNumber,
          sequenceNumber: stopNumber,
        };
      }
    }

    originalStopsData.push({
      stopId: stop.stopId,
      stopNumber: `${stop.stopNumber}`,
    });
    return stop;
  });

  return { originalStopsData, updatedStops };
};

export const stopWithValidStopNumber = (stop) => {
  const MAX_STOP_NUMBER = 999;
  const MAX_DISPLAY_NUMBER_LENGTH = 10;
  return (
    stop.stopNumber &&
    +stop.stopNumber <= MAX_STOP_NUMBER &&
    stop.displayNumber?.length <= MAX_DISPLAY_NUMBER_LENGTH
  );
};

export const hasInvalidStopNumbers = (stops) =>
  stops.some((stop) => !stopWithValidStopNumber(stop));
